import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../types';

const EMPTY_OBJECT: Record<string, never> = {};

export const orderSelectors = {
  isFullyPacked: createSelector(
    (state: RootState) => state.orders.itemsRemaining,
    (remainingItems) => {
      const itemsRemainingKeys = Object.keys(remainingItems);
      return !itemsRemainingKeys.some(
        (sku) => (remainingItems[sku].quantity ?? 0) > 0,
      );
    },
  ),
  selectedOrder: (state: RootState) => state.orders.selectedOrder,
  isFreight: createSelector(
    (state: RootState) => state.orders.selectedOrder,
    (selectedOrder): boolean => (selectedOrder as any)?.isFreight ?? false,
  ),
  packages: (state: RootState) => state.orders.packages || EMPTY_OBJECT,
  physicalItems: (state: RootState) =>
    state.orders.physicalItems || EMPTY_OBJECT,
  originalItems: (state: RootState) =>
    state.orders.originalItems || EMPTY_OBJECT,
  itemsRemaining: (state: RootState) =>
    state.orders.itemsRemaining || EMPTY_OBJECT,
  itemsCompleted: (state: RootState) =>
    state.orders.itemsCompleted || EMPTY_OBJECT,
  hasUnsealedPackage: (state: RootState) => state.orders.hasUnsealedPackage,
  hasUnsealedPallet: (state: RootState) => state.orders.hasUnsealedPallet,
  canSaveEmptyPalletDraft: createSelector(
    (state: RootState) => state.orders,
    ({ selectedOrder, hasPallets, packages, packageEditId }) => {
      if (!(selectedOrder as any)?.isFreight || !hasPallets) return false;
      const isEmpty = (pallet) =>
        !!pallet &&
        typeof pallet.metadata?.isPickAndShip === 'boolean' &&
        !Object.keys(pallet.items || {}).length &&
        !Object.keys(pallet.childPackages || {}).length;
      // Do not relax the existing restriction while editing a populated pallet.
      if (packageEditId && !isEmpty(packages[packageEditId])) return false;
      const drafts = Object.values(packages).filter(
        (pallet) => !pallet.metadata?.isCompleted,
      );
      return drafts.length > 0 && drafts.every(isEmpty);
    },
  ),
  hasPallets: (state: RootState) => state.orders.hasPallets,
  completedPackagesCount: (state: RootState) =>
    state.orders.completedPackagesCount,
  completedItemsQty: (state: RootState) => state.orders.completedItemsQty,
  rateId: (state: RootState) => state.orders.rateId,
  canOnlyShip: (state: RootState) => state.orders.canOnlyShip,
  isReship: (state: RootState) => state.orders.isReship,
  workstationId: (state: RootState) => state.orders.workstationId,
  palletId: (state: RootState) => state.orders.palletId,
};
